/* eslint-disable */
import { Loading, Modal, Toggle } from "@wfp/ui"
import { useEffect, useState, type ReactElement } from "react"
import Select from "react-select"
import { useUpdateSeriesMutation, useAddSeriesCombinationMutation } from "services/core/series"
import { SeriesCombination } from "interfaces/dataInterfaces"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useGetPriceTypeQuery } from "services/core/priceType"
import { useGetCommoditiesUnitQuery, useGetCommodityByCountryQuery } from "services/core/commodities"
import { useGetCurrenciesQuery } from "services/core/currencies"
import { useGetMarketsQuery } from "services/core/markets"
import { useGetCommoditiesQuery } from "services/core/commodities"
import useCommon from "hooks/useCommon"
import { useAssociatedCountriesQuery } from "services/core/user"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import { MARKET_MODULE } from "constants/constants"

interface MarketOptions {
  value: number
  label: string
}

interface ActionItem {
  item: SeriesCombination
  action: "edit" | "delete"
}

interface Props {
  actionItem: ActionItem | null
  showNewUserModal: boolean
  onClose: () => void
  adm0Code?: number
}

type Inputs = {
  country: number
  priceTypeID: number
  commodityUnitID: number
  currencyID: number
  marketID: MarketOptions | MarketOptions[]
  commodityIDs: number[]
  seriesCombinationVisibility: boolean
}

const marketSelectAll: MarketOptions = { label: "Select All", value: 0 }
const defaultQueryParams = { skip: 0, take: 0, query: "" } as any

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}

const AddSeries = ({ actionItem, onClose, showNewUserModal, adm0Code }: Props): ReactElement => {
  const { showSnackBar } = useCommon()

  const [admCode, setAdmCode] = useState<number | undefined>(adm0Code)

  const { handleSubmit, reset, setValue, control } = useForm<Inputs>()

  const [updateSeriesCombination] = useUpdateSeriesMutation()
  const [addSeriesCombination] = useAddSeriesCombinationMutation()
  const { data: countries } = useAssociatedCountriesQuery()
  const { data: priceType } = useGetPriceTypeQuery()
  const { data: currencies } = useGetCurrenciesQuery(defaultQueryParams)
  const { data: Commodities } = useGetCommoditiesQuery(defaultQueryParams)
  const { data: commoditiesUnit } = useGetCommoditiesUnitQuery(defaultQueryParams)
  const { data: markets, isFetching } = useGetMarketsQuery({
    adm0code: admCode ?? 0,
    skip: 0,
    top: 0,
    // query,
  })
  const { data: commoditiesCountry } = useGetCommodityByCountryQuery(
    {
      admCode: admCode ?? 0,
    },
    { skip: !admCode },
  )

  const countriesData: Array<{ value: number; label: string }> = []
  countries?.map(c => {
    countriesData.push({
      value: Number(c.adm0Code),
      label: c.countryName,
    })
  })
  const priceTypeData: Array<{ value: number; label: string }> = []
  priceType?.map(c => {
    priceTypeData.push({
      value: Number(c.priceTypeID),
      label: c.priceTypeName,
    })
  })
  const unitData: Array<{ value: number; label: string }> = []
  commoditiesUnit?.items?.map(c => {
    unitData.push({
      value: Number(c.commodityUnitID),
      label: c.commodityUnitName,
    })
  })
  const currenciesData: Array<{ value: number; label: string }> = []
  currencies?.items.map(c => {
    currenciesData.push({
      value: Number(c.currencyID),
      label: c.currencyName,
    })
  })
  const marketsData: Array<MarketOptions> = []
  if (actionItem?.action !== "edit") {
    marketsData.push(marketSelectAll)
  }
  markets?.items.map(c => {
    marketsData.push({
      value: Number(c.marketID),
      label: c.marketName,
    })
  })
  const commoditiesData: Array<{ value: number; label: string }> = []
  commoditiesCountry?.map(c => {
    commoditiesData.push({
      value: Number(c.commodityId),
      label: c.commodityName,
    })
  })

  const handleNewEditModalClose = () => {
    onClose()
    reset()
  }

  const onSubmit: SubmitHandler<Inputs> = async data => {
    try {
      if (actionItem?.action === "edit") {
        const marketIDs = [(data.marketID as MarketOptions)?.value]
        const response = await updateSeriesCombination({
          marketIDs,
          commodityIDs: data.commodityIDs,
          priceTypeID: data.priceTypeID,
          commodityUnitID: data.commodityUnitID,
          currencyID: data.currencyID,
          seriesCombinationID: actionItem.item.seriesCombinationID,
          seriesCombinationVisibility: data.seriesCombinationVisibility,
        }).unwrap()

        showSnackBar(
          response?.resultMessage || "Series updated successfully!",
          response?.resultCode === 0 ? "error" : "success",
        )
        onClose()
        reset()
      } else {
        const marketIDs = (data.marketID as MarketOptions[])?.map(m => m.value) as any
        const response = await addSeriesCombination({
          commodityIDs: data.commodityIDs,
          priceTypeID: data.priceTypeID,
          commodityUnitID: data.commodityUnitID,
          currencyID: data.currencyID,
          marketIDs,
          adm0Code: admCode,
          seriesCombinationVisibility: data.seriesCombinationVisibility,
        }).unwrap()

        showSnackBar(
          response?.resultMessage || "Series added successfully!",
          response?.resultCode === 0 ? "error" : "success",
        )
      }
    } catch (e: any) {
      showSnackBar(e.data?.substring(0, 50), "error")
    }
  }

  const commoditiesDataAdd: Array<{ value: number; label: string }> = []
  Commodities?.items?.map(c => {
    commoditiesDataAdd.push({
      value: Number(c.commodityId),
      label: c.commodityName,
    })
  })

  useEffect(() => {
    if (actionItem?.action === "edit") {
      setAdmCode(actionItem.item.adm0Code)
      setValue("country", actionItem.item.adm0Code)
      setValue("priceTypeID", actionItem.item.priceTypeID)
      setValue("commodityUnitID", actionItem.item.commodityUnitID)
      setValue("currencyID", actionItem.item.currencyID)
      setValue("marketID", marketsData.find(m => m.value === actionItem.item.marketID) as MarketOptions)
      setValue(
        "commodityIDs",
        commoditiesData?.filter(c => c.value === actionItem.item.commodityID).map(item => item.value),
      )
      setValue("seriesCombinationVisibility", actionItem.item.seriesCombinationVisibility)
    }
  }, [actionItem, admCode, marketsData, commoditiesData])

  return (
    <Modal
      open={showNewUserModal || actionItem?.action === "edit"}
      modalHeading={actionItem?.action === "edit" ? "Modify Series Combination" : "Insert new Series Combination"}
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit(onSubmit)}
      onRequestClose={handleNewEditModalClose}
    >
      <Loading active={isFetching} />
      <form>
        <div className="d-flex mt-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <label htmlFor="abc" className="wfp--label">
              Country
            </label>
            <div style={{ width: "100%" }}>
              <Controller
                {...{ control }}
                name="country"
                render={({ field: { onChange, ref } }) => (
                  <Select
                    isSearchable
                    className="wfp--react-select-container"
                    classNamePrefix="wfp--react-select"
                    ref={ref}
                    options={countriesData}
                    value={countriesData.find(r => r.value === admCode)}
                    onChange={value => {
                      onChange(value?.value as any)
                      setAdmCode(value?.value)
                    }}
                    isDisabled={actionItem?.action === "edit"}
                  />
                )}
              />
            </div>
          </div>

          <div style={{ width: "100%", marginLeft: "2%" }}>
            <label htmlFor="abc" className="wfp--label">
              Price Type
            </label>
            <Controller
              {...{ control }}
              name="priceTypeID"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={priceTypeData}
                  value={priceTypeData.find(r => r.value == value)}
                  onChange={value => onChange(value?.value as any)}
                />
              )}
            />
          </div>
        </div>

        <div className="d-flex mt-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <label htmlFor="abc" className="wfp--label">
              Market
            </label>
            <div style={{ width: "100%" }}>
              <Controller
                {...{ control }}
                name="marketID"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    isSearchable
                    className="wfp--react-select-container"
                    classNamePrefix="wfp--react-select"
                    ref={ref}
                    isMulti={actionItem?.action !== "edit"}
                    options={marketsData}
                    value={value as any}
                    onChange={value => {
                      if (actionItem?.action === "edit") {
                        onChange(value)
                        return
                      }

                      const isSelectedAll = value.find((v: MarketOptions) => v.value === 0)
                      onChange(isSelectedAll ? marketsData.filter((v: MarketOptions) => v.value !== 0) : value)
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="d-flex mt-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <label htmlFor="abc" className="wfp--label">
              Commodity
            </label>
            <div style={{ width: "100%" }}>
              <Controller
                {...{ control }}
                name="commodityIDs"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    className="wfp--react-select-container"
                    classNamePrefix="wfp--react-select"
                    ref={ref}
                    isMulti
                    isSearchable
                    options={actionItem?.action === "edit" ? commoditiesData : commoditiesDataAdd}
                    value={
                      actionItem?.action === "edit"
                        ? commoditiesData.filter(r => value?.includes(r.value))
                        : commoditiesDataAdd.filter(r => value?.includes(r.value))
                    }
                    onChange={values => onChange(values?.map(item => item.value))}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="d-flex mt-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <label htmlFor="abc" className="wfp--label">
              Unit of Measure
            </label>
            <Controller
              {...{ control }}
              name="commodityUnitID"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={unitData}
                  value={unitData.find(r => r.value === value)}
                  onChange={value => onChange(value?.value as any)}
                />
              )}
            />
          </div>

          <div style={{ width: "100%", marginLeft: "2%" }}>
            <label htmlFor="abc" className="wfp--label">
              Currency
            </label>
            <Controller
              {...{ control }}
              name="currencyID"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={currenciesData}
                  value={currenciesData.find(r => r.value === value)}
                  onChange={value => onChange(value?.value as any)}
                />
              )}
            />
          </div>
        </div>

        <PermissionWrapper {...permissionsProps} feature="edit">
          <div className="d-flex mt-2" style={{ width: "100%" }}>
            <div style={{ width: "100%" }}>
              <label htmlFor="seriesCombinationVisibility" className="wfp--label">
                Visibility
              </label>
              <Controller
                control={control}
                name="seriesCombinationVisibility"
                defaultValue={true}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    id="seriesCombinationVisibility"
                    toggled={!!value}
                    labelA=""
                    labelB={value ? "Public" : "Internal use"}
                    onToggle={(checked: boolean) => {
                      onChange(checked)
                    }}
                  />
                )}
              />
            </div>
          </div>
        </PermissionWrapper>
      </form>
    </Modal>
  )
}

export default AddSeries
